import { cn } from "cn";
import { Upload } from "lucide-react";
import type * as React from "react";
import { useId, useRef, useState } from "react";

// The v3.2 upload area (style.css .drop): a dashed panel to drop a file on
// or tap to choose one, with what it accepts underneath. One file at a time;
// the caller checks size and type and shows errors.
export function FileDrop({
  title,
  hint,
  accept,
  onFile,
  disabled = false,
  busy = false,
  icon,
  className,
  testId,
}: {
  title: React.ReactNode;
  /** What it accepts ("PDF, up to 10 MB"). */
  hint?: React.ReactNode;
  /** The input's accept list, e.g. "application/pdf". */
  accept?: string;
  onFile: (file: File) => void;
  disabled?: boolean;
  /** Shown while the file is read; the area does not take another. */
  busy?: boolean;
  icon?: React.ReactNode;
  className?: string;
  testId?: string;
}): React.JSX.Element {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const off = disabled || busy;
  const take = (files: FileList | null): void => {
    const file = files?.[0];
    if (file && !off) onFile(file);
  };
  return (
    <label
      aria-busy={busy || undefined}
      className={cn(
        "flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-[16px] border-[1.5px] border-dashed border-v3-border-strong bg-v3-surface-2 px-5 py-6 text-center font-v3 transition-colors hover:bg-v3-surface-3 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-v3-brand",
        over && "border-v3-brand bg-v3-brand-tint",
        off && "pointer-events-none opacity-60",
        className,
      )}
      data-dragging={over || undefined}
      data-slot="file-drop"
      data-testid={testId}
      htmlFor={inputId}
      onDragLeave={() => setOver(false)}
      onDragOver={(event) => {
        event.preventDefault();
        if (!off) setOver(true);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        take(event.dataTransfer.files);
      }}
    >
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-full bg-v3-panel text-v3-text-2 shadow-v3-hairline [&_svg]:size-5"
      >
        {icon ?? <Upload />}
      </span>
      <span className="text-[15px] font-semibold text-v3-text">{title}</span>
      {hint && (
        <span className="text-[13px] leading-[18px] text-v3-text-3">
          {hint}
        </span>
      )}
      <input
        ref={input}
        accept={accept}
        className="sr-only"
        disabled={off}
        id={inputId}
        type="file"
        onChange={(event) => {
          take(event.target.files);
          // The same file can be chosen again after an error.
          event.target.value = "";
        }}
      />
    </label>
  );
}
